import React, { useCallback, useContext, useMemo } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ritzDataContext, selectLoggedInAgent, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { getSubRouteAbsolutePath, PREVIEW_ENTITY_PARAM_NAME, RouteGroup, SubRoute, SubRouteNavBar } from '@moderepo/mode-app-components';
import { useRitzTranslation } from '../../utils/customHooks';
import { fillInRouteParams } from '../routes/RouteHelper';
import { ProjectTypeHandlersManager } from '../../utils/ritzExtensionUtils';


export const SubRouteNavBarContainer: React.FC<{
    readonly projectId: number | undefined;
}> = (props: {
    readonly projectId: number | undefined;
}) => {
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { state:dataState } = useContext(ritzDataContext);
    const project = props.projectId ? selectProjectById(dataState, props.projectId) : undefined;
    const loggedInAgent = selectLoggedInAgent(dataState);
    const { trans } = useRitzTranslation(project);


    const projectPaths = useMemo(() => {
        const routesHandler = ProjectTypeHandlersManager.getInstance().getRoutesHandler(project?.supportToolSettings.projectType);
        return routesHandler ? routesHandler.getRoutePaths(project, loggedInAgent) : {};
    }, [loggedInAgent, project]);


    // Go through all the subRoutes in the projectPaths and translate the subRoutes' 'label'
    const localizedProjectPaths = useMemo(() => {
        return Object.keys(projectPaths).reduce((result: {
            [name: string]: string | RouteGroup;
        }, key: string): {
            [name: string]: string | RouteGroup;
        } => {
            if (typeof projectPaths[key] === 'string') {
                return {
                    ...result,
                    [key]: projectPaths[key],
                };
            }

            // If projectPaths[key] is NOT a string then it must be a RouteGroup
            const routeGroup = projectPaths[key] as RouteGroup;
            return {
                ...result,
                [key]: {
                    ...routeGroup,
                    subRoutes: routeGroup.subRoutes.map((subRoute: SubRoute): SubRoute => {
                        return {
                            ...subRoute,
                            label: trans(subRoute.label),
                        };
                    }),
                } as RouteGroup,

            };
        }, {});
    }, [projectPaths, trans]);



    const onSubRouteSelected = useCallback((routeGroup: RouteGroup, subRoute: SubRoute) => {
        // Save the queryParams form the URL so we don't loose it and we need to add it back to the URL later
        // ALso, we need to remove the PREVIEW ENTITY info from the URL when the user navigate to another tab so that it will
        // hide the preview panel, if there is one showing.
        const searchParams = new URLSearchParams(window.location.search || '');
        searchParams.delete(PREVIEW_ENTITY_PARAM_NAME);

        navigate(
            `${fillInRouteParams(getSubRouteAbsolutePath(routeGroup, subRoute.name), params)}?${searchParams.toString()}`,
        );
    }, [navigate, params]);



    return (
        <SubRouteNavBar
            pathname={location.pathname}
            projectPaths={localizedProjectPaths}
            onSubRouteSelected={onSubRouteSelected}
        />
    );
};
